<template>
  <bottom-sheet
    :visible="visible"
    :close-icon-right-align="true"
    @close="closeModal"
  >
    <div
      v-if="showStaticModal"
      class="flex flex-col gap-3 px-3 pb-2"
    >
      <h4 class="-mt-5 text-xl text-steel-1100">Offer Placed</h4>

      <div class="flex items-center justify-between">
        <img
          v-if="brokerLogo"
          class="flex h-8 max-w-48 items-center text-2xs"
          :src="brokerLogo"
          alt="broker logo"
        />

        <div class="flex flex-col items-end gap-2 pt-2">
          <h3 class="text-3xl font-semibold">
            {{ formatPrice(load.price) }}
          </h3>
          <p
            v-if="ratePerMile && load?.price"
            class="-mt-2 text-1xs text-neutral-subtlest"
          >
            {{ ratePerMile }}/mi
          </p>
        </div>
      </div>

      <p class="font-normal text-steel-900">
        You've successfully placed an offer of
        {{ formatPrice(placed_offer?.rate) }}
        for load #{{ load?.external_order_or_load_number }} with
        {{ load?.broker_name }}. This broker's system does not support in-app
        negotiation. You need to contact them immediately to continue
        negotiations.
      </p>

      <p class="font-normal text-steel-900">
        Phone:
        {{
          formatPhoneParenthesis(
            load?.broker_phone_number,
            load?.broker_phone_extension
          ) || '-'
        }}
      </p>

      <p class="font-normal text-steel-900">
        Email:
        <a
          :href="`mailto:${load.broker_email}`"
          class="text-sun-700 underline"
        >
          {{ load.broker_email }}
        </a>
      </p>

      <div class="flex justify-end gap-2 [&>*]:flex-1 md:[&>*]:flex-initial">
        <ButtonComponent @click="handleTrackOffer">
          <router-link
            :to="{
              name: 'Offers',
              hash: `#history-${placed_offer?.id}`,
            }"
          >
            Track Your Offer
          </router-link>
        </ButtonComponent>
        <ButtonComponent
          variant="primary"
          @click="closeModal"
        >
          Done
        </ButtonComponent>
      </div>
    </div>

    <div
      v-else
      class="book-now-inputs flex flex-col gap-3 px-3"
      :class="{
        'max-h-[calc(100vh-100px)]': isMobile,
        'max-h-[80vh]': !isMobile,
      }"
    >
      <div class="-mt-4 text-left">
        <h4 class="text-xl text-steel-1100">Submit Offer</h4>
        <p class="text-steel-700">
          <span class="text-red-500">*</span>
          = Required Fields
        </p>
      </div>

      <div class="flex-between my-2">
        <img
          v-if="brokerLogo"
          class="flex h-8 max-w-48 items-center text-2xs"
          :src="brokerLogo"
          alt="broker logo"
        />

        <div class="flex flex-col items-end gap-2 pt-2">
          <h3 class="text-3xl font-semibold">
            {{ formatPrice(load.price) }}
          </h3>
          <p
            v-if="ratePerMile && load?.price"
            class="-mt-2 text-1xs text-neutral-subtlest"
          >
            {{ ratePerMile }}/mi
          </p>
        </div>
      </div>

      <div
        v-if="
          bookItNowType === 'offers_only_static' ||
          bookItNowType === 'offers_and_book_it_now_static'
        "
        class="flex gap-3 rounded-md border border-steel-300 px-4 py-3"
      >
        <img
          class="mt-0.5 h-5 w-5"
          src="@/assets/exclamation.svg"
          alt="exclamation"
        />

        <div>
          <p>Heads up!</p>
          <p class="text-sm font-normal text-steel-800">
            This broker's system cannot support in-app negotiation. You must
            call or email the broker immediately after submitting your offer to
            follow up.
          </p>

          <a
            href="https://service.highway.com/en/articles/13392157-how-to-manage-your-offers#h_15d4a7c891"
            target="_blank"
            class="cursor-pointer text-sm text-sun-700 underline"
          >
            Learn More
          </a>
        </div>
      </div>

      <div class="mb-4 flex flex-col gap-4">
        <p class="text-left text-lg">Booking Info</p>

        <div v-if="!use_posted_offer">
          <div class="flex justify-between">
            <p class="pb-1 text-left text-sm text-steel-900">
              Rate
              <span class="text-red-500">*</span>
            </p>

            <span
              v-if="supportsBoth"
              class="cursor-pointer text-left text-sm text-sun-800 hover:underline"
              @click="handleUsePostedOffer"
            >
              Use Posted Offer to Book Now
            </span>
          </div>
          <div class="rate-input relative">
            <p
              class="absolute left-2 top-1 z-20 mt-1 pl-2 text-left text-base font-bold text-steel-900"
            >
              $
            </p>
            <InputComponent
              input-label="rate"
              wrapper-class="flex bg-white text-sm items-center h-10 gap-1.5 pr-2 pl-[28px]"
              placeholder="Enter Rate"
              :default-input-value="form.rate"
              :value="form.rate"
              @input-change="handleInput"
              @on-blur="handleRateBlur"
            />
            <p
              class="absolute right-8 top-1.5 mt-1 pl-2 text-left font-light text-steel-900"
            >
              {{ inputRatePerMile }}/mi
            </p>
          </div>
          <p
            v-if="rate_error_text"
            class="mt-1 pl-2 text-left text-sm font-normal text-red-500"
          >
            {{ rate_error_text }}
          </p>
          <p
            v-else
            class="mt-1 pl-2 text-left text-sm font-normal text-steel-700"
          >
            Your offer amount
          </p>
        </div>

        <div
          v-else-if="supportsBoth"
          class="flex justify-between"
        >
          <div>
            <p class="pb-1 text-left text-sm text-steel-900">
              Rate
              <span class="text-red-500">*</span>
            </p>
            <div class="flex items-center gap-2">
              <p class="text-base font-bold">${{ form.rate }}</p>
              <p class="font-normal text-steel-800">
                {{ inputRatePerMile }}/mi
              </p>
            </div>
          </div>

          <span
            class="cursor-pointer text-left text-sm text-sun-800 hover:underline"
            @click="handleNameMyOffer"
          >
            Name My Offer
          </span>
        </div>

        <div v-if="canSRCD">
          <p class="text-left text-base text-steel-900">
            Rate Confirmation Email
          </p>

          <div class="flex items-center gap-2 pt-1">
            <p class="text-left font-normal text-steel-900">
              Sent securely through Highway.
            </p>

            <a
              href="https://help.highway.com/en/articles/10910720-secure-rate-confirmation-delivery"
              target="_blank"
            >
              <img
                src="@/assets/question-mark.svg"
                alt="question mark"
                currentColor="#000"
              />
            </a>
          </div>
        </div>

        <div
          v-else
          class="relative"
        >
          <p class="pl-2 text-left text-sm font-semibold text-steel-800">
            Rate Confirmation Email
            <span class="text-red-500">*</span>
          </p>
          <div
            class="relative flex h-10 w-full items-center rounded-lg border border-steel-300"
            :class="{
              'h-16 bg-steel-100': form.ratecon_email,
              'h-10 bg-white': !form.ratecon_email,
              'cursor-pointer': !isEditingOffer,
            }"
            @click="toggleRateConDropdown"
          >
            <p class="pl-2 text-sm text-steel-700">{{ form.ratecon_email }}</p>

            <img
              v-if="!isEditingOffer"
              class="absolute right-3 opacity-50"
              src="@/assets/arrow-down-black.svg"
              alt="carrot"
            />
          </div>
          <div
            v-if="showRateConDropdown"
            ref="emailsRef"
            class="absolute z-20 mt-1 flex max-h-[200px] w-full flex-col overflow-scroll rounded-lg border border-steel-300 bg-white py-2"
          >
            <div
              v-for="email in ratecon_emails"
              :key="email.id"
              class="flex cursor-pointer flex-col items-start border-l-4 border-transparent px-3 py-2 hover:border-sun-400 hover:bg-sun-50"
              :class="{
                '!border-sun-400 bg-sun-50': email.id === form.ratecon_email_id,
              }"
              @click="handleSelectEmail(email)"
            >
              <p class="text-sun-800 underline">{{ email.value }}</p>
            </div>
          </div>
        </div>

        <p class="text-left text-base text-steel-900">Dispatching Info</p>

        <div
          ref="dispatchRef"
          class="relative"
        >
          <div
            class="relative flex h-10 w-full items-center rounded-lg border border-steel-300"
            :class="{
              'h-20 bg-steel-100 py-3': form.dispatch_name,
              'h-10 bg-white': !form.dispatch_name,
              'cursor-pointer': !isEditingOffer,
            }"
            @click="toggleContactsDropdown"
          >
            <div class="flex flex-col items-start">
              <p class="pl-2 text-sm font-bold text-steel-900">
                {{ form.dispatch_name }}
              </p>
              <p class="pl-2 text-sm font-light text-steel-900">
                {{ form.dispatch_email }}
              </p>
              <p class="pl-2 text-sm font-light text-steel-900">
                {{ form.dispatch_phone }}
              </p>
            </div>

            <img
              v-if="!isEditingOffer"
              class="absolute right-3 opacity-50"
              src="@/assets/arrow-down-black.svg"
              alt="carrot"
            />
          </div>
          <div
            v-if="showContactsDropdown"
            class="absolute z-20 mt-1 flex max-h-[200px] w-full flex-col overflow-scroll rounded-lg border border-steel-300 bg-white py-2"
          >
            <div>
              <InputComponent
                input-label="dispatch_search"
                wrapper-class="flex  text-sm items-center gap-1.5 p-2 mx-4 my-2"
                placeholder="Search Verified Users"
                :default-input-value="form.dispatch_search"
                :value="form.dispatch_search"
                @input-change="handleSearchContacts"
              >
                <template #leftIcon>
                  <img
                    class="pt-1"
                    src="@/assets/search-light-gray.svg"
                    alt="filters"
                  />
                </template>
              </InputComponent>

              <div v-if="contacts?.length > 0">
                <div
                  v-for="contact in contacts"
                  :key="contact.id"
                  class="flex cursor-pointer flex-col items-start border-l-4 border-transparent px-3 py-1 hover:border-sun-400 hover:bg-sun-50"
                  @click="hanelSelectContact(contact)"
                >
                  <p class="font-semibold capitalize text-steel-800">
                    {{ contact.name }}
                  </p>
                  <p class="text-sun-800 underline">
                    {{ contact.email_address?.value }}
                  </p>
                  <p class="text-xs font-normal capitalize text-steel-800">
                    {{ contact.phone?.value }}
                  </p>
                </div>
              </div>

              <div
                v-else
                class="p-3"
              >
                <p class="text-steel-700">No Matching contacts.</p>
              </div>
            </div>
          </div>
        </div>

        <div
          ref="vehicleSearchRef"
          class="relative"
          :class="{
            'cursor-not-allowed opacity-40': !use_posted_offer,
          }"
        >
          <p class="mb-4 mt-0 text-left text-base text-steel-900">
            Vehicle
            <span
              v-if="use_posted_offer"
              class="text-red-500"
            >
              *
            </span>
          </p>

          <div
            class="relative flex h-10 w-full items-center rounded-lg border border-steel-300"
            :class="{
              'h-16 bg-steel-100': form.vehicle?.id,
              'h-10 bg-white': !form.vehicle?.id,
              'cursor-pointer': !isEditingOffer,
              '[&_*]:cursor-not-allowed': !use_posted_offer,
            }"
            @click="openVehicleDropdown"
          >
            <InputComponent
              v-if="!form.vehicle?.id"
              input-label="test"
              wrapper-class="flex bg-white w-full text-sm items-center h-10 gap-1.5 px-2"
              placeholder="Choose Vehicle"
              :is-disabled="!use_posted_offer"
              :default-input-value="form.vehicle_query"
              :value="form.vehicle_query"
              @input-change="handleVehicleSearchInput"
            />

            <div
              v-else
              class="flex w-full items-center justify-between px-4 py-2 pr-10"
              :class="{
                'cursor-pointer': !isEditingOffer,
              }"
            >
              <div class="flex items-start gap-2">
                <img
                  class="mt-1 h-5 w-5"
                  src="@/assets/loads/green-wifi.svg"
                  alt="wifi"
                />

                <div class="text-left">
                  <div class="flex justify-between">
                    <div class="text-left">
                      <p class="text-sm text-steel-1100">
                        {{ form?.vehicle?.truck_number || form?.vehicle.id }}
                      </p>
                    </div>
                    <div
                      v-if="form?.vehicle?.license_plate_number"
                      class="ml-2 text-right"
                    >
                      <p class="text-xs text-steel-700">
                        Plate #: {{ form?.vehicle.license_plate_number }}
                        <span v-if="form?.vehicle?.license_plate_state">
                          ({{ form?.vehicle.license_plate_state }})
                        </span>
                      </p>
                    </div>
                  </div>
                  <p class="text-xs text-steel-700">
                    {{ form?.vehicle.vehicle?.vin }}
                  </p>
                </div>
              </div>
            </div>

            <img
              v-if="form.vehicle?.id && !isEditingOffer"
              class="absolute right-4 h-2.5 opacity-60 hover:opacity-40"
              src="@/assets/close-icon.svg"
              alt="close"
              @click="clearVehicle"
            />
          </div>

          <div
            v-if="showVehicleDropdown"
            class="absolute z-20 mt-1 flex max-h-[200px] w-full flex-col overflow-scroll rounded-lg border border-steel-300 bg-white py-2"
          >
            <div v-if="isCarrierVehiclesLoading">
              <img
                class="loading m-auto my-4 h-8 w-8"
                src="@/assets/loading-v2-yellow.svg"
                alt="loading"
              />
            </div>
            <div v-else-if="carrier_vehicles?.length > 0">
              <div
                v-for="vehicle in carrier_vehicles"
                :key="vehicle.id"
                class="flex cursor-pointer items-center justify-between px-4 py-2 hover:bg-steel-100"
                @click="handleSelectVehicle(vehicle)"
              >
                <div class="flex items-start gap-2">
                  <img
                    class="mt-1 h-5 w-5"
                    src="@/assets/loads/green-wifi.svg"
                    alt="wifi"
                  />

                  <div class="text-left">
                    <div class="flex justify-between">
                      <div class="text-left">
                        <p class="text-sm text-steel-1100">
                          {{ vehicle?.truck_number || vehicle.id }}
                        </p>
                      </div>
                      <div
                        v-if="vehicle?.license_plate_number"
                        class="ml-2 text-right"
                      >
                        <p class="text-xs text-steel-700">
                          Plate #: {{ vehicle.license_plate_number }}
                          <span v-if="vehicle?.license_plate_state">
                            ({{ vehicle.license_plate_state }})
                          </span>
                        </p>
                      </div>
                    </div>
                    <p class="text-xs text-steel-700">
                      {{ vehicle.vehicle?.vin }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else
              class="p-3"
            >
              <p class="text-steel-700">
                There are no current vehicles to select.
              </p>
            </div>
          </div>
        </div>

        <div>
          <p class="pb-1 text-left text-sm text-steel-900">Trailer Number</p>

          <InputComponent
            v-if="!isEditingOffer"
            input-label="trailer_number"
            wrapper-class="flex bg-white text-sm items-center h-10 gap-1.5 px-2"
            placeholder="Enter Trailer Number"
            :default-input-value="form.trailer_number"
            :value="form.trailer_number"
            @input-change="handleInput"
          />

          <div
            v-else
            class="flex h-10 w-full items-center rounded-md border border-steel-300 bg-steel-100 px-3"
          >
            <p class="text-steel-800">
              {{ form.trailer_number || '-' }}
            </p>
          </div>
        </div>
      </div>

      <p class="-mb-2 font-normal text-steel-700">
        {{ offerMessage }}
      </p>

      <div class="my-4 flex justify-end gap-2">
        <ButtonComponent
          v-if="!isEditingOffer"
          class="flex h-12 w-1/2 min-w-[100px] justify-center md:h-10 md:w-min"
          @click="$emit('close')"
        >
          Not Now
        </ButtonComponent>
        <ButtonComponent
          v-if="isEditingOffer"
          class="flex w-1/2 min-w-[150px] border border-steel-300 !text-red-500 md:w-min"
          @click="$emit('withdraw')"
        >
          Withdraw Offer
        </ButtonComponent>
        <ButtonComponent
          variant="primary"
          class="flex w-1/2 !min-w-[150px] md:w-min"
          :is-loading="isLoading"
          :disabled="isSubmitButtonDisabled"
          @click="handleBookNow"
        >
          {{ buttonText }}
        </ButtonComponent>
      </div>
    </div>
  </bottom-sheet>
</template>

<script setup>
import { ref, computed, onBeforeMount, watch } from 'vue';
import { useStore } from 'vuex';
import debounce from 'lodash.debounce';
import dayjs from 'dayjs';
import { BottomSheet, InputComponent, ButtonComponent } from '@/components';
import { BookLoadNotification } from '@/components/loads';
import { useScreenSize } from '@/composables';
import {
  formatPrice,
  formatPriceWithoutMoneySign,
  useClickAway,
} from '@/utils';
import { formatPhoneParenthesis } from '@/common';

const store = useStore();

const props = defineProps({
  visible: Boolean,
  loadId: [String, Number],
});

const emit = defineEmits(['close', 'withdraw']);

const { screenSize } = useScreenSize();

const isMobile = computed(() => screenSize.value === 'mobile');

const form = ref({
  rate: '',
  ratecon_email: '',
  ratecon_email_id: '',
  dispatch_name: '',
  dispatch_phone: '',
  dispatch_email: '',
  trailer_number: '',
  driver_name: '',
  driver_phone: '',
  vehicle: {},
  vehicle_query: '',
});

const currentUser = computed(() => store.state.users?.currentUser);
const load = computed(() => store.state.loads.load_details);

const rateConEmail = ref('');

const bookItNowType = computed(() => load?.value?.book_it_now_type);
const isStaticType = computed(
  () =>
    bookItNowType.value === 'offers_only_static' ||
    bookItNowType.value === 'offers_and_book_it_now_static'
);
const showStaticModal = ref(false);

const supportsBoth = computed(() => {
  const hasType =
    bookItNowType.value === 'offers_and_book_it_now' ||
    bookItNowType.value === 'offers_and_book_it_now_static';
  const hasPrice = !!load?.value?.price;

  return hasType && hasPrice;
});

const placed_offer = computed(() => store.state.loads?.offer);
const isEditingOffer = computed(
  () =>
    placed_offer?.value?.id &&
    placed_offer?.value?.status !== 'rejected' &&
    placed_offer?.value?.status !== 'expired'
);

const use_posted_offer = ref(false);

const handleUsePostedOffer = () => {
  use_posted_offer.value = !use_posted_offer.value;

  form.value.rate = formatPriceWithoutMoneySign(load?.value?.price, 0);
};

const handleNameMyOffer = () => {
  handleUsePostedOffer();

  form.value.rate = '';
};

// check if company paywall can use Secure Rate Confirmation Delivery
const canSRCD = computed(
  () =>
    load?.value?.can_rate_confirmation &&
    store.state.users.featureFlags?.fe_display_ratecon_checkbox?.enabled
);

const carrier_vehicles = computed(
  () => store.state.loads?.eld_carrier_vehicles
);

const driver_same_as_dispatch = ref(true);

onBeforeMount(async () => {
  const carrier_id =
    currentUser?.value?.requested_carrier_id ||
    currentUser?.value?.company?.carrier_id;

  const [carrierDetailRes, emailRes] = await Promise.all([
    store.dispatch('carriers/getCarriersDetail', {
      id: carrier_id,
    }),
    store.dispatch('emails/getEmails', {
      query: `?q[carrier_id_eq]=${carrier_id}&q[is_active_eq]=1&q[s]=value+asc`,
    }),
    store.dispatch('contacts/getContactsByCarrierId', {
      carrierId: carrier_id,
    }),
    store.dispatch('loads/getCarrierVehicles', {
      id: carrier_id,
      query: `&q[without_active_truck_postings]=1&q[is_eld_active_eq]=1&q[s]=truck_number+asc`,
    }),
  ]);

  const dispatchConactInfo =
    carrierDetailRes?.data?.contact_information?.dispatch_contact;

  const findCurrentUserEmail = emailRes?.find(
    (el) =>
      el.value === store.state.users?.currentUser?.email &&
      el?.is_type === 'rate_confirmation'
  );

  const findEmail = emailRes?.find((el) => el?.is_type === 'rate_confirmation');

  const initialEmail = findCurrentUserEmail || findEmail || emailRes[0];

  const carrierRateConEmail = `dot${carrierDetailRes?.data?.dot_number}@highway.com`;
  rateConEmail.value = carrierRateConEmail;

  const email = canSRCD?.value
    ? carrierRateConEmail
    : store.state.users?.currentUser?.email;

  form.value.rate = formatPriceWithoutMoneySign(load?.value?.price, 0);
  form.value.ratecon_email = email;
  form.value.ratecon_email_id = initialEmail?.id;
  form.value.dispatch_name = dispatchConactInfo?.name;
  form.value.dispatch_phone = dispatchConactInfo?.phone;
  form.value.dispatch_email = dispatchConactInfo?.email_address;

  if (load?.value?.book_it_now_type === 'book_it_now_only') {
    if (load?.value?.price) {
      use_posted_offer.value = true;
    } else {
      use_posted_offer.value = false;
    }
  } else if (load?.value?.book_it_now_type === 'offers_and_book_it_now') {
    if (!load?.value?.price) {
      use_posted_offer.value = false;
    } else {
      use_posted_offer.value = true;
    }
  }

  if (isEditingOffer?.value) {
    const findOfferVehicle = carrier_vehicles.value?.find(
      (vehicle) => vehicle?.id === placed_offer.value?.carrier_vehicle_id
    );

    if (findOfferVehicle) {
      form.value.vehicle = findOfferVehicle;
    }

    form.value.rate = formatPriceWithoutMoneySign(placed_offer?.value?.rate, 0);
    form.value.trailer_number = placed_offer.value?.trailer_number;

    if (placed_offer.value?.driver_name !== placed_offer.value?.dispatch_name) {
      driver_same_as_dispatch.value = false;

      form.value.driver_name = placed_offer.value?.driver_name;
      form.value.driver_phone = placed_offer.value?.driver_phone;
    }
  }
});

const brokerLogo = computed(() => load?.value?.broker_logo?.url);
const ratePerMile = computed(() => {
  return formatPrice(load?.value?.rate_per_mile);
});

const inputRatePerMile = computed(() => {
  if (
    parseInt(form.value.rate?.replace(',', '')) === parseInt(load.value?.price)
  ) {
    return formatPrice(load?.value?.rate_per_mile);
  } else {
    const updatedRate =
      parseInt(form.value?.rate?.replace(',', '')) /
      parseInt(load.value?.posted_miles);
    return formatPrice(updatedRate);
  }
});

const isCarrierVehiclesLoading = computed(
  () => store.state.loads?.is_carrier_vehicles_loading
);

const showVehicleDropdown = ref(false);
const openVehicleDropdown = () => {
  if (isEditingOffer.value || !use_posted_offer.value) {
    return;
  }
  showVehicleDropdown.value = true;
};
const toggleVehicleDropdown = () => {
  showVehicleDropdown.value = !showVehicleDropdown.value;
};

const vehicleSearchRef = ref();
useClickAway(vehicleSearchRef, () => {
  showVehicleDropdown.value = false;
});

const handleVehicleSearchInput = debounce(async (value) => {
  form.value.vehicle_query = value;
  showVehicleDropdown.value = true;

  await store.dispatch('loads/getCarrierVehicles', {
    id: store.state.users?.currentUser?.requested_carrier_id,
    query: `&q[without_active_truck_postings]=1&q[is_eld_active_eq]=1&q[s]=truck_number+asc&q[truck_number_or_license_plate_number_or_vehicle_vin_start]=${value}`,
  });
}, 500);

const clearVehicle = async () => {
  form.value.vehicle_query = '';
  form.value.vehicle = {};

  setTimeout(() => {
    showVehicleDropdown.value = false;
  }, 0);

  await store.dispatch('loads/getCarrierVehicles', {
    id: store.state.users?.currentUser?.requested_carrier_id,
    query: `&q[without_active_truck_postings]=1&q[is_eld_active_eq]=1&q[s]=truck_number+asc`,
  });
};

const handleSelectVehicle = (vehicle) => {
  form.value.vehicle = vehicle;
  toggleVehicleDropdown();
};

const showRateConDropdown = ref(false);
const toggleRateConDropdown = () => {
  if (isEditingOffer.value) {
    return;
  }

  showRateConDropdown.value = !showRateConDropdown.value;
};
const handleSelectEmail = (email) => {
  form.value.ratecon_email = email?.value;
  form.value.ratecon_email_id = email?.id;
  toggleRateConDropdown();
};

const emailsRef = ref(null);
useClickAway(emailsRef, () => {
  showRateConDropdown.value = false;
});

const ratecon_emails = computed(() => {
  const emails = store.state.emails?.emails ?? [];

  // Deduplicate by `value`
  const unique = Array.from(new Map(emails.map((e) => [e.value, e])).values());

  // Find current user's rate_confirmation email
  const currentUserEmail = store.state.users?.currentUser?.email;
  const firstIndex = unique.findIndex(
    (e) => e.is_type === 'rate_confirmation' && e.value === currentUserEmail
  );

  if (firstIndex > -1) {
    const [currentUserEmailObj] = unique.splice(firstIndex, 1);
    unique.unshift(currentUserEmailObj);
  }

  // Sort so rate_confirmation types come first
  return unique.sort((a, b) => {
    if (a.is_type === 'rate_confirmation' && b.is_type !== 'rate_confirmation')
      return -1;
    if (b.is_type === 'rate_confirmation' && a.is_type !== 'rate_confirmation')
      return 1;
    return 0;
  });
});

const showContactsDropdown = ref(false);
const toggleContactsDropdown = () => {
  if (isEditingOffer.value) {
    return;
  }

  showContactsDropdown.value = !showContactsDropdown.value;
};
const hanelSelectContact = (contact) => {
  form.value.dispatch_name = contact.name;
  form.value.dispatch_email = contact.email_address?.value;
  form.value.dispatch_phone = contact.phone?.value;
  toggleContactsDropdown();
};

const dispatchRef = ref(null);
useClickAway(dispatchRef, () => {
  showContactsDropdown.value = false;
});

const contacts = computed(() => {
  const dispatching_contacts = store.state.contacts?.contacts;

  return Array.from(
    new Map(dispatching_contacts.map((e) => [e.value, e])).values()
  );
});

const handleSearchContacts = debounce(async (text) => {
  await store.dispatch('contacts/getContactsByCarrierId', {
    carrierId: currentUser?.value?.requested_carrier_id,
    nameQuery: text,
  });
}, 300);

watch(driver_same_as_dispatch, (newValue) => {
  if (newValue) {
    form.value.driver_name = form.value.dispatch_name;
    form.value.driver_phone = form.value.dispatch_phone;
  }
});

watch(use_posted_offer, (newValue) => {
  if (!newValue) {
    form.value.vehicle = {};
  }
});

const offerMessage = computed(() =>
  use_posted_offer?.value
    ? 'Once a load booking is confirmed, your vehicle may be removed from active Post Capacity.'
    : 'You will receive a rate confirmation email once your offer is accepted by the broker.'
);

const buttonText = computed(() =>
  isEditingOffer?.value
    ? 'Edit Offer'
    : use_posted_offer?.value
      ? 'Confirm Booking'
      : 'Confirm Offer'
);

const isLoading = computed(() => store.state.loads?.is_book_now_loading);

const rate_error = ref(false);
const rate_error_text = ref('');

const debouncedRate = debounce((val) => {
  // raw digits only
  const digits = val.replace(/[^\d]/g, '');

  if (!digits) {
    form.value.rate = '';
    return;
  }

  // convert to number
  const num = parseInt(digits, 10);

  if (load.value.price && bookItNowType.value !== 'offers_only') {
    const isRateGreaterThanLoadPrice = num > +load.value?.price;

    if (!isRateGreaterThanLoadPrice) {
      rate_error.value = true;
    } else {
      rate_error.value = false;
      rate_error_text.value = '';
    }
  } else {
    if (num > 0) {
      rate_error.value = false;
    } else {
      rate_error.value = true;
    }
  }

  // format with commas (no decimals here, but you could add them if you want)
  form.value.rate = num.toLocaleString('en-US');
}, 200);

const handleRateBlur = () => {
  if (rate_error.value) {
    if (load.value.price && bookItNowType.value !== 'offers_only') {
      rate_error_text.value = `Your offer must be greater than the broker's posted rate of ${formatPrice(load.value.price)}`;
    } else {
      rate_error_text.value = 'Your offer must be greater than $0.';
    }
  } else {
    rate_error_text.value = '';
  }
};

const handleInput = (value, name) => {
  if (name === 'rate') {
    debouncedRate(value);
  } else {
    form.value[name] = value;
  }
};

const isSubmitButtonDisabled = computed(() => {
  if (!use_posted_offer.value) {
    return !form.value?.ratecon_email || rate_error.value || !form.value.rate;
  } else {
    return (
      !form.value?.ratecon_email ||
      !form.value?.vehicle?.id ||
      rate_error.value ||
      !form.value.rate
    );
  }
});

const handleBookNow = async () => {
  console.log('Handle book now selection', form?.value);

  const params = {
    carrier_vehicle_id: form.value.vehicle?.id,
    rate_confirmation_email_address_id: form.value?.ratecon_email_id,
    dispatch_name: form.value?.dispatch_name,
    dispatch_email: form.value?.dispatch_email,
    dispatch_phone: form.value?.dispatch_phone,
    driver_name: driver_same_as_dispatch?.value
      ? form.value?.dispatch_name
      : form.value?.driver_name,
    driver_phone: driver_same_as_dispatch?.value
      ? form.value?.dispatch_phone
      : form.value?.driver_phone,
    trailer_number: form.value?.trailer_number,
    ...(canSRCD?.value ? { secure_rate_confirmation: true } : {}),
  };

  const expiresAtTime = dayjs().add(24, 'hour').format('YYYY-MM-DD HH:mm:ss');

  let title;
  let subText;
  let res;
  if (isEditingOffer.value) {
    res = await store.dispatch('loads/editOffer', {
      id: placed_offer?.value?.id,
      params: {
        ...params,
        rate: parseInt(form.value.rate?.replace(',', '')),
        load_id: props?.loadId,
        expires_at: expiresAtTime,
      },
    });

    title = 'Offer Updated';
    subText = "You've successfully updated this offer.";
  } else if (use_posted_offer?.value) {
    // book now request
    res = await store.dispatch('loads/bookNow', {
      id: props?.loadId,
      params: {
        ...params,
        id: props?.loadId,
      },
    });

    title = 'Load Booked';
    subText = "You've successfully booked this load.";
  } else {
    // place offer request
    res = await store.dispatch('loads/placeOffer', {
      id: props?.loadId,
      params: {
        ...params,
        rate: parseInt(form.value.rate?.replace(',', '')),
        load_id: props?.loadId,
        expires_at: expiresAtTime,
      },
    });

    title = 'Offer Placed';
    subText = "You've successfully placed an offer on this load.";
  }

  if ([200, 201].includes(res.status)) {
    await store.dispatch('toast/addToast', {
      component: BookLoadNotification,
      props: {
        visible: true,
        type: 'success',
        title,
        subText,
      },
      duration: 10000,
    });

    use_posted_offer.value = true;

    if (isStaticType.value) {
      showStaticModal.value = true;
    } else {
      emit('close');
    }
  } else {
    await store.dispatch('toast/addToast', {
      component: BookLoadNotification,
      props: {
        visible: true,
        type: 'error',
      },
      duration: 10000,
    });
    emit('close');

    setTimeout(() => {
      store.commit('loads/setBookNowError', {});
    }, 10000);
  }
};

const closeModal = () => {
  showStaticModal.value = false;
  emit('close');
};

const handleTrackOffer = () => {
  localStorage.setItem('current_offer', JSON.stringify(placed_offer?.value));
};
</script>

<style>
.booknow-checkbox .container {
  @apply w-fit pl-9;
}
.booknow-checkbox .container > div {
  @apply font-normal text-steel-900;
}
.booknow-checkbox .container .checkbox-checkmark {
  @apply h-5 w-5;
}
.booknow-checkbox .container .checkbox-checkmark:after {
  border-color: black;
  width: 6px;
  height: 10px;
  border-width: 0 2px 2px 0;
  top: 4px !important;
  left: 7px;
}

.booknow-checkbox .container input:checked ~ .checkbox-checkmark {
  border-color: transparent;
}

.book-now-inputs input {
  @apply text-sm;
}

.rate-input input {
  @apply text-base !font-bold;
}
</style>
